/**
 * Create Wallet Form
 *
 * Form to create a new hot wallet with WebAuthn PRF
 *
 * Note: WebAuthn PRF requires two-step verification by design:
 * 1. First verification creates the credential
 * 2. Second verification derives the encryption key via PRF output
 * This is a limitation of the WebAuthn PRF spec, not a bug.
 */

import { useState } from "react";
import {
  ArrowLeft,
  Fingerprint,
  Loader2,
  Shield,
  Lock,
  Check,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  useBrowserWallet,
  useBrowserWalletStore,
} from "@nullaone/wallet-react";

interface VerifyWord {
  index: number;
  word: string;
}

interface CreateWalletFormState {
  name: string;
  mnemonic: string | null;
  step: "name" | "step1" | "step2" | "mnemonic" | "verify";
  verifyWords: VerifyWord[];
  userInputs: string[];
}

/**
 * Generate 3 random word indices for verification
 */
function generateVerifyWords(mnemonic: string): VerifyWord[] {
  const words = mnemonic.split(" ");
  const indices = new Set<number>();

  // Pick 3 random unique indices
  while (indices.size < 3) {
    indices.add(Math.floor(Math.random() * words.length));
  }

  // Sort indices and create verify word objects
  return Array.from(indices)
    .sort((a, b) => a - b)
    .map((index) => ({ index, word: words[index]! }));
}

export function CreateWalletForm() {
  const { t } = useTranslation();
  const { createWallet, connecting, closeModal } = useBrowserWallet();
  const { setModalStep } = useBrowserWalletStore();

  const [state, setState] = useState<CreateWalletFormState>({
    name: "",
    mnemonic: null,
    step: "name",
    verifyWords: [],
    userInputs: ["", "", ""],
  });

  const handleCreate = async () => {
    setState((prev) => ({ ...prev, step: "step1" }));

    try {
      const result = await createWallet(state.name || undefined, (step) => {
        if (step === "creating") {
          setState((prev) => ({ ...prev, step: "step1" }));
        } else if (step === "encrypting") {
          setState((prev) => ({ ...prev, step: "step2" }));
        }
      });
      setState((prev) => ({
        ...prev,
        mnemonic: result.mnemonic,
        step: "mnemonic",
      }));
    } catch {
      setState((prev) => ({ ...prev, step: "name" }));
    }
  };

  const isCreating = state.step === "step1" || state.step === "step2";

  const handleContinueToVerify = () => {
    if (!state.mnemonic) return;
    const verifyWords = generateVerifyWords(state.mnemonic);
    setState((prev) => ({
      ...prev,
      step: "verify",
      verifyWords,
      userInputs: ["", "", ""],
    }));
  };

  const handleBackToMnemonic = () => {
    setState((prev) => ({ ...prev, step: "mnemonic" }));
  };

  const handleVerified = () => {
    closeModal();
  };

  if (state.step === "verify" && state.mnemonic) {
    return (
      <VerifyStep
        verifyWords={state.verifyWords}
        userInputs={state.userInputs}
        setUserInputs={(inputs) =>
          setState((prev) => ({ ...prev, userInputs: inputs }))
        }
        onVerified={handleVerified}
        onBack={handleBackToMnemonic}
      />
    );
  }

  if (state.step === "mnemonic" && state.mnemonic) {
    return (
      <MnemonicStep
        mnemonic={state.mnemonic}
        onContinue={handleContinueToVerify}
      />
    );
  }

  // Show verification progress during creation
  if (isCreating) {
    return (
      <div className="space-y-6 py-4">
        <div className="text-center py-8 space-y-6">
          {/* Step indicator */}
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              {t("wallet.create.progress.stepOf", {
                current: state.step === "step1" ? 1 : 2,
                total: 2,
              })}
            </p>
            <Progress
              value={state.step === "step1" ? 40 : 80}
              className="h-2"
            />
          </div>

          {/* Step-specific UI */}
          {state.step === "step1" ? (
            <div className="space-y-4">
              <div className="flex justify-center">
                <div className="relative">
                  <Shield className="h-16 w-16 text-primary" />
                  <Loader2 className="absolute -top-1 -right-1 h-6 w-6 animate-spin text-primary" />
                </div>
              </div>
              <div className="space-y-1">
                <p className="font-medium text-lg">
                  {t("wallet.create.progress.step1Title")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("wallet.create.progress.step1Desc")}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-center">
                <div className="relative">
                  <Lock className="h-16 w-16 text-primary" />
                  <Loader2 className="absolute -top-1 -right-1 h-6 w-6 animate-spin text-primary" />
                </div>
              </div>
              <div className="space-y-1">
                <p className="font-medium text-lg">
                  {t("wallet.create.progress.step2Title")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("wallet.create.progress.step2Desc")}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 py-4">
      <Button
        variant="ghost"
        size="sm"
        className="gap-2"
        onClick={() => setModalStep("select")}
      >
        <ArrowLeft className="h-4 w-4" />
        {t("wallet.create.back")}
      </Button>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="wallet-name">{t("wallet.create.nameLabel")}</Label>
          <Input
            id="wallet-name"
            placeholder={t("wallet.create.namePlaceholder")}
            value={state.name}
            onChange={(e) =>
              setState((prev) => ({ ...prev, name: e.target.value }))
            }
          />
        </div>

        {/* Two-step verification info */}
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
          <div className="flex items-start gap-3">
            <Shield className="h-6 w-6 text-primary mt-0.5 shrink-0" />
            <div className="space-y-2">
              <p className="text-sm font-medium">
                {t("wallet.create.twoStepTitle")}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("wallet.create.twoStepIntro")}
              </p>
              <ol className="text-xs text-muted-foreground list-decimal list-inside space-y-1 ml-1">
                <li>{t("wallet.create.twoStepStep1")}</li>
                <li>{t("wallet.create.twoStepStep2")}</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-muted/50 p-4">
          <div className="flex items-start gap-3">
            <Fingerprint className="h-6 w-6 text-muted-foreground mt-0.5 shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-medium">
                {t("wallet.create.biometricTitle")}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("wallet.create.biometricDesc")}
              </p>
            </div>
          </div>
        </div>

        <Button
          className="w-full"
          size="lg"
          onClick={handleCreate}
          disabled={connecting}
        >
          <Fingerprint className="mr-2 h-4 w-4" />
          {t("wallet.create.submit")}
        </Button>
      </div>
    </div>
  );
}

interface MnemonicStepProps {
  mnemonic: string;
  onContinue: () => void;
}

function MnemonicStep({ mnemonic, onContinue }: MnemonicStepProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const words = mnemonic.split(" ");

  const handleCopy = async () => {
    await navigator.clipboard.writeText(mnemonic);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6 py-4">
      <div className="rounded-lg border border-warning/40 bg-warning/10 p-4">
        <p className="text-sm text-warning-dark">
          <strong>{t("wallet.create.mnemonic.warningLabel")}</strong>{" "}
          {t("wallet.create.mnemonic.warningBody")}
        </p>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {words.map((word, index) => (
          <div
            key={index}
            className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2 text-sm"
          >
            <span className="text-xs text-muted-foreground w-4">
              {index + 1}.
            </span>
            <span className="font-mono">{word}</span>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" onClick={handleCopy}>
          {copied
            ? t("wallet.create.mnemonic.copied")
            : t("wallet.create.mnemonic.copy")}
        </Button>
      </div>

      <p className="text-xs text-muted-foreground text-center">
        {t("wallet.create.mnemonic.hint")}
      </p>

      <Button className="w-full" size="lg" onClick={onContinue}>
        {t("wallet.create.mnemonic.continue")}
      </Button>
    </div>
  );
}

interface VerifyStepProps {
  verifyWords: VerifyWord[];
  userInputs: string[];
  setUserInputs: (inputs: string[]) => void;
  onVerified: () => void;
  onBack: () => void;
}

function VerifyStep({
  verifyWords,
  userInputs,
  setUserInputs,
  onVerified,
  onBack,
}: VerifyStepProps) {
  const { t } = useTranslation();
  // Check if all inputs match the expected words (case-insensitive)
  const isValid = verifyWords.every(
    (w, i) =>
      userInputs[i]?.toLowerCase().trim() === w.word.toLowerCase().trim(),
  );

  const handleInputChange = (index: number, value: string) => {
    const newInputs = [...userInputs];
    newInputs[index] = value;
    setUserInputs(newInputs);
  };

  return (
    <div className="space-y-6 py-4">
      <div className="text-center space-y-2">
        <p className="font-medium text-lg">{t("wallet.create.verify.title")}</p>
        <p className="text-sm text-muted-foreground">
          {t("wallet.create.verify.desc")}
        </p>
      </div>

      <div className="space-y-4">
        {verifyWords.map((item, i) => (
          <div key={item.index} className="space-y-2">
            <Label htmlFor={`word-${item.index}`}>
              {t("wallet.create.verify.wordLabel", { n: item.index + 1 })}
            </Label>
            <Input
              id={`word-${item.index}`}
              placeholder={t("wallet.create.verify.wordPlaceholder", {
                n: item.index + 1,
              })}
              value={userInputs[i]}
              onChange={(e) => handleInputChange(i, e.target.value)}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
            />
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <Button variant="outline" onClick={onBack} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          {t("wallet.create.verify.back")}
        </Button>
        <Button
          className="flex-1 gap-2"
          disabled={!isValid}
          onClick={onVerified}
        >
          <Check className="h-4 w-4" />
          {t("wallet.create.verify.submit")}
        </Button>
      </div>
    </div>
  );
}
